import { ReactElement } from "react";
import { T, TF } from "react-translator-component";
import { colors } from "../../../common/colors";
import dayjs from "../../../util/dayjs";
import {
  dispPaymentType,
  dispPayUserType
} from "../../util/payments";
import { formatEventDate } from "../../util/posts";
import CopyText from "../CopyText";
import DisplayModal from "../DisplayModal";

interface Props {
  open: boolean;
  payment: any;
  onClose: () => void;
}

type ChildTableData = {
  title: string,
  value: string | number
}

export default function PaymentDetailModal({open, payment, onClose}: Props): ReactElement {
  const renderPaymentPartyRow = (key: "payer" | "payee") => {
    const childTableData: ChildTableData[] = [
      {title: T(`mng_payment_${key}_type`), value: dispPayUserType(payment?.[key]?.type)},
      {
        title:
          TF(`mng_payment_${key}_name_title`,
            T(`mng_${payment?.[key]?.type === "MemberGroup" ? "member_group" : "user"}`)
          ),
        value: payment?.[key]?.nickname ?? payment?.[key]?.name
      },
      {title: T(`mng_payment_digital_id`), value: payment?.[key]?.digital_id}
    ]

    return (
      <tr style={styles.bottomLine}>
        <th>{T(`mng_payment_${key}_info`)}</th>
        {renderChildTable(key, childTableData)}
      </tr>
    )
  }

  const renderPostEventRow = () => {
    return (
      <>
        {payment?.post_event && (
          <tr style={styles.bottomLine}>
            <th style={styles.cellTitle}>{T("mng_post_event")}</th>
            <td style={styles.cell}>
              {payment?.post_event?.title}
              <br />
              <span style={styles.eventData}>
                {formatEventDate(
                  payment?.post_event?.start_date,
                  payment?.post_event?.end_date
                )}
              </span>
              <br />
              <span style={styles.eventData}>
                {payment?.post_event?.postal_code}
              </span>
              <br />
              <span style={styles.eventData}>
                {payment?.post_event?.address}
              </span>
              <br />
              <span style={styles.eventData}>
                {payment?.post_event?.venue_name}
              </span>
            </td>
          </tr>
        )}
      </>
    )
  }

  const renderReimbursementRow = () => {
    const childTableData: ChildTableData[] = [
      {title: T("mng_reimbursement_payment_date"), value: dayjs(payment?.reimbursement?.payment_date).format("YYYY/MM/DD")},
      {title: T("mng_reimbursement_payment_address"), value: payment?.reimbursement?.payment_address},
      {title: T("mng_payment_amount"), value: payment?.reimbursement?.amount?.toLocaleString()},
    ]

    return (
      <>
        {payment?.reimbursement && (
          <tr style={styles.bottomLine}>
            <th style={styles.cellTitle}>{T("mng_reimbursement_detail")}</th>
            {renderChildTable("reimbursement", childTableData)}
          </tr>
        )}
      </>
    )
  }

  const renderChildTable = (prefix: string, dataList: ChildTableData[]) => {
    return (
      <td style={styles.childCell}>
        <table style={styles.table}>
          <tbody>
            {dataList.map((data: ChildTableData, index: number) => (
              <tr style={index === dataList.length - 1 ? {} : styles.bottomLine} key={`${prefix}_${index}`}>
                <th style={styles.childCellTitle}>{data.title}</th>
                <td style={styles.childCellData}>{data.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    )
  }

  return (
    <DisplayModal
      title={`${T("mng_payment_detail")}`}
      open={payment && open ? true : false}
      sx={styles.container}
      onClose={onClose}
    >
      <div style={styles.tableContainer}>
        <table style={styles.table} border={1}>
          <tbody>
            <tr style={styles.bottomLine}>
              <th style={styles.cellTitle}>{T("mng_payment_type")}</th>
              <td style={styles.cell}>
                {dispPaymentType(payment?.payment_type)}
              </td>
            </tr>

            {renderPaymentPartyRow("payer")}

            {renderPaymentPartyRow("payee")}

            <tr style={styles.bottomLine}>
              <th style={styles.cellTitle}>{T("mng_payment_amount")}</th>
              <td style={styles.cell}>
                {payment?.formatted_amount?.toLocaleString()}
              </td>
            </tr>

            <tr style={styles.bottomLine}>
              <th style={styles.cellTitle}>{T("mng_payment_details")}</th>
              <td style={styles.cell}>
                {payment?.payment_details}
              </td>
            </tr>

            {renderReimbursementRow()}

            {renderPostEventRow()}

            <tr style={styles.bottomLine}>
              <th style={styles.cellTitle}>{T("mng_payment_transaction_id")}</th>
              <td style={styles.cell}>
                <CopyText text={payment?.transaction_id} />
              </td>
            </tr>

            <tr style={styles.bottomLine}>
              <th style={styles.cellTitle}>{T("mng_payment_date")}</th>
              <td style={styles.cell}>
                {dayjs(payment?.created_at).format("YYYY/MM/DD HH:mm")}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DisplayModal>
  );
}

const styles = {
  tableContainer: {
    maxHeight: "calc(100vh*0.9*0.7 - 90px)",
    overflowY: "auto" as const,
  },

  table: {
    width: "100%",
    borderCollapse: "collapse" as const
  },

  cell: {
    padding: 10,
    border: "1px solid black",
    textAlign: "left" as const
  },

  childCell: {
    padding: 0,
    border: "1px solid black",
    textAlign: "left" as const
  },

  cellTitle: {
    width: "100px",
  },

  childCellTitle: {
    fontWeight: "normal",
    width: "170px",
    paddingLeft: 10,
  },

  childCellData: {
    padding: 10,
    borderLeft: `1px solid ${colors.farm_black}`,
    overflowWarp: "anywhere",
    lineBreak: "anywhere" as const,
  },

  container: {
    maxWidth: "85%",
    paddingX: 2,
    paddingBottom: 3,
    textAlign: "center",
  },

  bottomLine: {
    borderBottom: `1px solid ${colors.farm_black}`,
  },

  eventData: {
    color: colors.farm_gray200,
    fontSize: 12
  },
}